import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { CancelIcon, MailOutlineIcon, PlayArrowIcon, RefreshIcon, RestartAltIcon } from "../icons";
import { PageLayout } from "../common/layout";
import { useRootStore } from "../store/contexts/RootStoreContext";
import {
  SimulationControlStore,
  SIM_GATES,
  type GatePlan,
} from "./simulation/SimulationControlStore";
import { registerSimulationSSEHandlers } from "./simulation/simulationSSEHandlers";
import { WorkflowRoadmap } from "../common/controls/WorkflowRoadmap";
import {
  signedEditionDndaWorkflow,
  signedEditionSadaicWorkflow,
} from "../workflows/workflowDefinitions";
import { SectionCard } from "./common/SectionCard";
import { StatusChip } from "./common/StatusChip";
import { RetryIndicator } from "./common/RetryIndicator";
import { CircuitTimeline, type TimelineEntry } from "./common/CircuitTimeline";

// URL de la UI de Mailpit (sandbox). En prod: https://mailpit.jupiter.ar
// (traefik + basic-auth, solo Fase 1) vía REACT_APP_MAILPIT_URL en el build.
const MAILPIT_URL = process.env.REACT_APP_MAILPIT_URL ?? "http://localhost:8025";

const DNDA_ERROR_STATES = [
  "ejemplarGeneracionFallida",
  "registroDndaFallido",
  "uploadFallido",
];

const errText = (error: any): string =>
  error ? String(error?.message?.es ?? error?.message ?? error ?? "").slice(0, 160) : "";

export const SimulationPage = observer(() => {
  const rootStore = useRootStore();
  const store = useMemo(() => new SimulationControlStore(rootStore), [rootStore]);
  const isSysAdmin = !!(rootStore.authStore as any)?.hasRole?.("SysAdmin");

  useEffect(() => {
    if (!isSysAdmin) return;
    // Navegación contextual (?editionId=...): retomar/preseleccionar ESA edición.
    const editionId = new URLSearchParams(window.location.search).get("editionId") ?? undefined;
    void store.loadEditions();
    void store.loadActiveSessions(editionId); // retomar una simulación en curso al recargar
    const cleanup = registerSimulationSSEHandlers(rootStore, store);
    return cleanup;
  }, [store, rootStore, isSysAdmin]);

  // Poll mientras haya sesión activa (liveness aunque el SSE falle).
  useEffect(() => {
    if (!store.session) return;
    const t = setInterval(() => {
      if (store.shouldPoll) store.scheduleRefresh();
    }, 30000);
    return () => clearInterval(t);
  }, [store, store.session?.id]);

  if (!isSysAdmin) {
    return (
      <PageLayout>
        <Box sx={{ p: 3 }}>
          <Typography variant="h2">403</Typography>
          <Typography color="text.secondary">Esta herramienta es solo para SysAdmin.</Typography>
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Stack spacing={2} sx={{ p: 3, width: "100%" }}>
        <Box>
          <Typography variant="h2">Simulación del circuito DNDA → SADAIC</Typography>
          <Typography variant="body2" color="text.secondary">
            Sin tocar servicios externos reales (FTP/email van al sandbox; el portal DNDA se
            simula). Solo tenant AGILMIND, SysAdmin.
          </Typography>
        </Box>

        {store.error && (
          <Alert severity="error" onClose={() => (store.error = null)}>
            {store.error}
          </Alert>
        )}

        {!store.session ? <SetupPanel store={store} /> : <LivePanel store={store} />}
      </Stack>
    </PageLayout>
  );
});

const SetupPanel = observer(({ store }: { store: SimulationControlStore }) => {
  return (
    <SectionCard
      title="Nueva simulación"
      subtitle="Se elige una edición y el plan por gate; con «mostrar todas» se puede continuar desde cualquier estado o resetear."
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 360 }}>
            <InputLabel id="sim-edition-label">Edición</InputLabel>
            <Select
              labelId="sim-edition-label"
              label="Edición"
              value={store.selectedEditionId ?? ""}
              onChange={(e) => store.selectEdition((e.target.value as string) || null)}
            >
              <MenuItem value="">
                <em>— elegir —</em>
              </MenuItem>
              {store.editions.map((ed: any) => {
                const name = ed.data_?.name;
                const shortId = (ed.displayId ?? ed.id ?? "").toString().slice(0, 8);
                const state = ed.dndaState?.id;
                return (
                  <MenuItem key={ed.id} value={ed.id}>
                    {name ? `${name} — ${shortId}` : ed.displayId ?? ed.id}
                    {store.showAllEditions && state ? `  [${state}]` : ""}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <Tooltip title="Recargar ediciones">
            <IconButton onClick={() => void store.loadEditions()} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <FormControlLabel
            control={
              <Checkbox
                checked={store.showAllEditions}
                onChange={(e) => store.setShowAllEditions(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                mostrar todas (continuar desde cualquier estado)
              </Typography>
            }
          />
        </Stack>

        <Box>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Modo
          </Typography>
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Checkbox checked={store.mode === "manual"} onChange={() => store.setMode("manual")} />
              }
              label={<Typography variant="body2">Manual (pausa en cada gate)</Typography>}
            />
            <FormControlLabel
              control={<Checkbox checked={store.mode === "auto"} onChange={() => store.setMode("auto")} />}
              label={<Typography variant="body2">Auto (avanza solo)</Typography>}
            />
          </Stack>
        </Box>

        <Box>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Plan por gate
          </Typography>
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Paso del circuito</TableCell>
                <TableCell>Gate</TableCell>
                <TableCell>Acción planificada</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SIM_GATES.map((g) => (
                <TableRow key={g.key}>
                  <TableCell>{g.label}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {g.key}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={store.plan[g.key] ?? "advance"}
                      onChange={(e) => store.setPlan(g.key, e.target.value as GatePlan)}
                    >
                      <MenuItem value="advance">Avanzar OK</MenuItem>
                      <MenuItem value="recoverable">Fallar (recuperable → retry worker)</MenuItem>
                      <MenuItem value="intervention">Fallar (terminal → transición de fallo)</MenuItem>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
        </Box>

        {/* Fila única de acciones: la primaria y su alternativa juntas. */}
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            disabled={!store.selectedEditionId || store.starting}
            onClick={() => void store.start()}
          >
            {store.starting ? "Iniciando…" : "Iniciar simulación"}
          </Button>
          {store.selectedEditionId && store.showAllEditions && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<RestartAltIcon />}
              onClick={() => {
                if (
                  window.confirm(
                    "¿Resetear la edición al estado inicial del circuito? (aborta sesiones activas y limpia los campos DNDA/SADAIC)"
                  )
                ) {
                  void store.resetEdition(store.selectedEditionId!);
                }
              }}
            >
              Reset a inicial
            </Button>
          )}
        </Stack>
      </Stack>
    </SectionCard>
  );
});

const LivePanel = observer(({ store }: { store: SimulationControlStore }) => {
  const s = store.session!;
  const gate = store.currentGate;

  const timelineEntries: TimelineEntry[] = [
    ...store.workerEvents.map((w: any) => ({
      at: new Date(w.enqueuedAt),
      title: w.jobType,
      status: w.status,
      detail:
        [
          (w.retryCount ?? 0) > 0 ? `reintento ${w.retryCount}/5` : null,
          w.nextRetryAt && ["queued", "processing"].includes(w.status)
            ? `próximo ~${new Date(w.nextRetryAt).toLocaleTimeString()}`
            : null,
          errText(w.error) || null,
        ]
          .filter(Boolean)
          .join(" · ") || undefined,
    })),
    ...store.events.map((e) => ({ at: e.at, title: e.label, status: "gate" })),
  ];

  return (
    <Stack spacing={2}>
      {/* 1. Header de la sesión */}
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="h2">{store.editionName ?? "Edición"}</Typography>
        <StatusChip status={s.status} size="medium" />
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: "monospace" }}>
          {((s as any).id ?? "").slice(0, 8)}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refrescar">
          <IconButton onClick={() => void store.refresh()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Button
          size="small"
          variant="outlined"
          startIcon={<MailOutlineIcon />}
          href={MAILPIT_URL}
          target="_blank"
          rel="noreferrer"
        >
          Mailpit
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<CancelIcon />}
          onClick={() => void store.abort()}
        >
          Abortar sesión
        </Button>
      </Stack>

      {/* 2. AHORA — lo accionable primero */}
      {store.isWaiting && gate && (
        <Alert severity="warning">
          <AlertTitle>El circuito está esperando tu decisión — gate «{gate}»</AlertTitle>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            <Button variant="contained" size="small" onClick={() => void store.control(gate, "advance")}>
              Avanzar
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              onClick={() => void store.control(gate, "fail", "recoverable")}
            >
              Fallar (recuperable)
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={() => void store.control(gate, "fail", "intervention")}
            >
              Fallar (terminal)
            </Button>
            <Button size="small" color="error" onClick={() => void store.control(null, "abort")}>
              Abortar
            </Button>
          </Stack>
        </Alert>
      )}
      {!store.isWaiting && (
        <Alert severity={store.activity.includes("falló") ? "error" : "info"}>
          {store.activity || "Procesando…"}
        </Alert>
      )}

      {/* 3. Progreso de los workflows */}
      <SectionCard title="Progreso del circuito">
        <Stack spacing={2}>
          {store.dndaState && (
            <Box>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography variant="h4">Workflow DNDA</Typography>
                <Typography variant="body2" color="text.secondary">
                  estado real: <code>{store.dndaState}</code>
                </Typography>
              </Stack>
              <WorkflowRoadmap
                workflow={signedEditionDndaWorkflow}
                currentState={store.dndaMilestone ?? store.dndaState}
                labels={{}}
                transitions={[]}
                onTransition={() => {}}
                errorStates={DNDA_ERROR_STATES}
              />
            </Box>
          )}
          {store.sadaicState && (
            <Box>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography variant="h4">Workflow SADAIC</Typography>
                <Typography variant="body2" color="text.secondary">
                  estado real: <code>{store.sadaicState}</code>
                </Typography>
              </Stack>
              <WorkflowRoadmap
                workflow={signedEditionSadaicWorkflow}
                currentState={store.sadaicMilestone ?? store.sadaicState}
                labels={{}}
                transitions={[]}
                onTransition={() => {}}
                errorStates={["subidaSadaicFallida"]}
              />
            </Box>
          )}
        </Stack>
      </SectionCard>

      {/* 4. Cronología (qué pasó, en qué orden) */}
      <SectionCard title="Cronología" subtitle="Jobs del worker y eventos de la simulación, en orden.">
        <CircuitTimeline entries={timelineEntries} />
      </SectionCard>

      {/* 5. Detalle: WorkerEvents */}
      <SectionCard
        title="Jobs del worker"
        subtitle="Cada paso del circuito corre como job; acá se ven sus reintentos y errores (los bots simulados no crean BotRun: este es su tracking)."
      >
        {store.workerEvents.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            — sin jobs para esta edición —
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Job</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Reintentos</TableCell>
                <TableCell>Error</TableCell>
                <TableCell align="right">Encolado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {store.workerEvents.map((w: any) => (
                <TableRow key={w.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {w.jobType}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={w.status} />
                  </TableCell>
                  <TableCell>
                    <RetryIndicator
                      retryCount={w.retryCount}
                      manualRetryCount={w.manualRetryCount}
                      nextRetryAt={["queued", "processing"].includes(w.status) ? w.nextRetryAt : null}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    {errText(w.error) ? (
                      <Tooltip title={errText(w.error)}>
                        <Typography variant="body2" color="error" noWrap>
                          {errText(w.error)}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={new Date(w.enqueuedAt).toLocaleString()}>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(w.enqueuedAt).toLocaleTimeString()}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
        )}
      </SectionCard>

      {/* 6. Detalle: BotRuns (bots con orquestador) */}
      <SectionCard
        title="Ejecuciones de bot"
        subtitle="Bots que corren con el orquestador real (emailDnda, uploadSadaic) — con fases y tracking completo."
      >
        {store.botRuns.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            — sin ejecuciones aún (aparecen al llegar a email DNDA / SADAIC) —
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Bot</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Fase actual</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {store.botRuns.map((b: any) => (
                <TableRow key={b.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {b.botType ?? "bot"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={b.status} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {b.currentPhase ?? "—"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </TableContainer>
        )}
      </SectionCard>

      <Box>
        <Button variant="text" onClick={() => store.reset()}>
          ← Nueva simulación
        </Button>
      </Box>
    </Stack>
  );
});
